import * as React from "react";

import * as journal from "@client/features/journal";
import * as search from "@client/features/search";

import * as graphics from "@client/app/graphics";
import * as router from "@client/router";
import * as analytics from "@client/shared/analytics";
import * as utils from "@client/utils";

import {calculateEntryMetrics, getRoundedCalculations} from "@client/calculations";
import {DaySummary} from "@client/components/DaySummary";
import {EntryList} from "@client/components/EntryList";
import {EntryMetrics} from "@client/components/EntryMetrics";
import {Flush} from "@client/components/Flush";
import {MealCard} from "@client/components/MealCard";
import {Scene} from "@client/components/Scene";
import {RecipeShare} from "@client/templates/Share";
import {server} from "@reactivated";

const WalkingClubShareRedirect = ({code}: {code: string}) => {
    React.useEffect(() => {
        // Keep the query string: the gate reads ?email= (and demo prefills).
        router.unsafeTransitionByUrl(
            `/club/${code}/${window.location.search}`,
            "replace",
        );
    }, []);
    return null;
};

export const Share = router.Share.useConditions((props) => {
    const {share} = props.resolved;
    const useKilojoules = props.profile?.use_kilojoules ?? false;
    const hiddenMacros = props.profile?.has_hidden_macros ?? false;

    React.useEffect(() => {
        const attribution = {
            attribution_type: "share" as const,
            share_type: share.type,
            share_uuid: share.share_uuid,
            referrer: null,
        };
        analytics.register(attribution);
        void server.core.rpc.attribute_share(attribution);
    }, []);

    switch (share.type) {
        case "profile": {
            return {
                options: {
                    title: "Profile",
                },
                view: <h1>Shared Profiles Coming Soon</h1>,
                actions: [],
            };
        }
        case "day": {
            const {day} = share;
            const actions = [
                props.profile == null
                    ? {
                          link: router.reverse("login.index", {}),
                          icon: "plus" as const,
                          track: "add_shared_day",
                          title: "Add to Journal",
                      }
                    : {
                          icon: "copy" as const,
                          title: "Copy to Journal",
                          onClick: () =>
                              props.dispatch(journal.pickDateAndCopyDay(day)),
                      },
            ];

            return {
                options: {
                    title: utils.formatDate(day.key),
                },
                actions,
                view: (
                    <Flush>
                        {day.meals
                            .filter((meal) => meal.name != "__SUPPLEMENTS__")
                            .map((meal) => (
                                <div key={meal.uuid}>
                                    <MealCard
                                        meal={meal}
                                        hiddenMacros={hiddenMacros}
                                        defaultExpanded={true}
                                    />
                                </div>
                            ))}
                        {hiddenMacros === false && (
                            <DaySummary
                                useKilojoules={useKilojoules}
                                day={day}
                                target={day.target}
                            />
                        )}
                    </Flush>
                ),
            };
        }
        case "meal": {
            const {meal} = share;

            const actions = [
                props.profile == null
                    ? {
                          link: router.reverse("login.index", {}),
                          icon: "plus" as const,
                          track: "add_shared_meal",
                          title: "Add to Journal",
                      }
                    : {
                          icon: "copy" as const,
                          title: "Copy to Journal",
                          onClick: () =>
                              props.dispatch(journal.pickDateAndCopyMeal(meal)),
                      },
            ];

            return {
                actions,
                options: {
                    title: meal.name,
                },
                view: <EntryList entries={meal.entries} hiddenMacros={hiddenMacros} />,
            };
        }
        case "recipe": {
            const bottomSection = (
                <div className="px-3 py-1">
                    <div className="flex items-center gap-2">
                        <div className="flex flex-1 items-center gap-4">
                            <graphics.Logo className="size-12" />

                            <div
                                className="
                                    flex items-center space-x-2 text-pretty
                                    max-sm:-ml-2 max-sm:space-x-1 max-sm:text-xs
                                "
                            >
                                <span className="text-gray-700">Nutrition</span>
                                <div className="size-1 rounded-full bg-indigo-600" />
                                <span className="text-gray-700">Workouts</span>
                                <div className="size-1 rounded-full bg-indigo-600" />
                                <span className="text-gray-700">Habits</span>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => {
                                    const url = `${utils.getBaseURLWithOriginForCapacitor()}/share/${share.share_uuid}/`;
                                    navigator
                                        .share({title: share.recipe.name, url})
                                        .then(() => {})
                                        .catch(() => {});
                                }}
                                type="button"
                                className="
                                    rounded-md bg-white px-2.5 py-1.5 text-sm
                                    font-semibold whitespace-nowrap text-gray-900
                                    shadow-xs ring-1 ring-gray-300 ring-inset
                                    hover:bg-gray-50
                                "
                            >
                                Share
                            </button>
                            {share.is_favorite == false ? (
                                <button
                                    onClick={() => {
                                        if (props.profile == null) {
                                            void analytics.track(
                                                "REDIRECTED_TO_LOGIN_FROM_SHARE",
                                                {
                                                    recipe_name: share.recipe.name,
                                                },
                                            );
                                            router.push("login.index", {});
                                            return;
                                        }

                                        void analytics.track("SAVED_SHARED_RECIPE", {
                                            recipe_name: share.recipe.name,
                                        });

                                        void props.dispatch(
                                            search.loadSearch({reset: true}),
                                        );
                                        void server.journal.nutrition.toggle_recipe_favorite(
                                            {
                                                form: share.recipe.uuid,
                                            },
                                        );
                                        props.setResolved(() => ({
                                            ...props.resolved,
                                            share: {
                                                ...props.resolved.share,
                                                is_favorite: true,
                                            },
                                        }));
                                        props.notify("Saved to your recipes");
                                    }}
                                    type="button"
                                    className="
                                        rounded-md bg-indigo-600 px-2.5 py-1.5 text-sm
                                        font-semibold whitespace-nowrap text-white
                                        shadow-xs
                                        hover:bg-indigo-500
                                        focus-visible:outline-2
                                        focus-visible:outline-offset-2
                                        focus-visible:outline-indigo-600
                                        focus-visible:outline-solid
                                    "
                                >
                                    Save
                                </button>
                            ) : (
                                <button
                                    onClick={() => {
                                        void server.journal.nutrition.toggle_recipe_favorite(
                                            {
                                                form: share.recipe.uuid,
                                            },
                                        );
                                        void props.dispatch(
                                            search.loadSearch({reset: true}),
                                        );

                                        void analytics.track("UNSAVED_SHARED_RECIPE", {
                                            recipe_name: share.recipe.name,
                                        });
                                        props.setResolved(() => ({
                                            ...props.resolved,
                                            share: {
                                                ...props.resolved.share,
                                                is_favorite: false,
                                            },
                                        }));
                                    }}
                                    type="button"
                                    className="
                                        rounded-md bg-indigo-600 px-2.5 py-1.5 text-sm
                                        font-semibold whitespace-nowrap text-white
                                        opacity-70 shadow-xs
                                        hover:bg-indigo-500
                                        focus-visible:outline-2
                                        focus-visible:outline-offset-2
                                        focus-visible:outline-indigo-600
                                        focus-visible:outline-solid
                                    "
                                >
                                    Saved
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            );

            return {
                options: {
                    title: share.recipe.name,
                    titleSection: "Shared Recipe",
                    scene: {kind: "MENU_ONLY"},
                },
                view: (
                    <Scene
                        actions={[]}
                        tabs={[]}
                        topSection={<></>}
                        bottomSection={bottomSection}
                        more={[]}
                        locator={null}
                    >
                        <div className="-mt-4">
                            <RecipeShare share={share} type="embed" />
                        </div>
                    </Scene>
                ),
                actions: [],
            };
        }
        case "entry": {
            const {entry} = share;
            const calculations = getRoundedCalculations(
                calculateEntryMetrics({
                    entry,
                    quantity: entry.quantity,
                    unit: entry.unit,
                }),
                useKilojoules,
            );

            const ingredientsTab =
                entry.expanded_recipe.length > 0
                    ? [
                          {
                              icon: "cutlery",
                              title: "Recipe",
                              view: (
                                  <EntryList
                                      entries={entry.expanded_recipe}
                                      hiddenMacros={hiddenMacros}
                                  />
                              ),
                          } as const,
                      ]
                    : [];

            return {
                options: {
                    title: entry.name,
                },
                tabs: [
                    {
                        icon: "bullseye",
                        title: "Macros",
                        // TODO: needs refactor to show full stage so that we can
                        // hide entry metrics from hidden macro people..
                        view: <EntryMetrics calculations={calculations} />,
                    },
                    ...ingredientsTab,
                ],
                actions: [],
            };
        }
        case "walking_club": {
            return {
                options: {
                    title: "Walking club",
                },
                view: <WalkingClubShareRedirect code={share.share_uuid} />,
                actions: [],
            };
        }
        case "missing": {
            return {
                options: {
                    title: "Share",
                },
                view: (
                    <div
                        className="
                            flex flex-1 flex-col items-center justify-center px-4
                            text-center
                        "
                    >
                        <h3 className="mb-1.5 text-[27px] leading-[1.3] font-light">
                            This link isn&rsquo;t active
                        </h3>
                        <p
                            className="
                                text-[14px] leading-relaxed font-light text-stone-600
                            "
                        >
                            Double-check the link you were sent, or ask for a fresh one.
                        </p>
                    </div>
                ),
                actions: [],
            };
        }
    }

    const _exhaustiveCheck: never = share;
    return _exhaustiveCheck;
});

export const ShareDebug = router.ShareDebug.useRedirect((props) => {
    return router.locator({
        route: "share.index",
        params: {
            share_uuid: props.params.share_uuid,
        },
        method: "replace",
    });
});
